/**
 * ProcessingJobsContext — global async construction job tracker.
 *
 * QuickProcessPanel adds a job when the BE returns 202 queued.
 * The context polls /jobs/{process_id} until the job completes, fails,
 * or needs input, and exposes a floating indicator the user can open
 * to see progress or jump to the resulting process map.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { getJobStatus, type IdeaToProcessResult, type JobStatusResponse } from "@/services/api/deliverableApi";
import type { ApiResponse } from "@/services/api/kernelBotApi";
import { toast } from "sonner";

export interface ProcessingJob {
  processId: string;
  callId: string;
  description: string;
  status: JobStatusResponse["status"];
  result: IdeaToProcessResult | null;
  error: string | null;
  startedAt: number;
  completedAt?: number;
}

interface ProcessingJobsContextValue {
  jobs: ProcessingJob[];
  addJob: (processId: string, callId: string, description: string) => void;
  removeJob: (processId: string) => void;
  openPanel: boolean;
  setOpenPanel: (open: boolean) => void;
}

const ProcessingJobsContext = createContext<ProcessingJobsContextValue | null>(null);

const POLL_INTERVAL_MS = 3000;
const COMPLETED_JOB_TTL_MS = 30000; // keep completed jobs visible for 30s

export function ProcessingJobsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<ProcessingJob[]>([]);
  const [openPanel, setOpenPanel] = useState(false);
  const timersRef = useRef<Record<string, NodeJS.Timeout>>({});

  const removeJob = useCallback((processId: string) => {
    if (timersRef.current[processId]) {
      clearInterval(timersRef.current[processId]);
      delete timersRef.current[processId];
    }
    setJobs((prev) => prev.filter((j) => j.processId !== processId));
  }, []);

  const stopPolling = useCallback((processId: string) => {
    if (timersRef.current[processId]) {
      clearInterval(timersRef.current[processId]);
      delete timersRef.current[processId];
    }
  }, []);

  const handleTerminalState = useCallback(
    (job: ProcessingJob, result: IdeaToProcessResult | null, error: string | null) => {
      stopPolling(job.processId);

      if (error || result?.status === "failed") {
        toast.error("Process construction failed", {
          description: error || result?.message || "Unknown error",
        });
      } else if (result?.status === "needs_input") {
        toast.warning("Idea needs clarification", {
          description: result.assessment?.idea_type
            ? `Classified as "${result.assessment.idea_type}".`
            : "Could not resolve the idea.",
        });
      } else if (result?.process_id) {
        toast.success("Process ready", {
          description: `${result.summary?.bots_created ?? 0} bots, ${result.summary?.nodes ?? 0} nodes`,
        });
        // Auto-dismiss after TTL, but leave the job visible briefly so the user can click it.
        setTimeout(() => removeJob(job.processId), COMPLETED_JOB_TTL_MS);
      }
    },
    [removeJob, stopPolling],
  );

  const pollJob = useCallback(
    async (processId: string) => {
      try {
        const apiRes = (await getJobStatus(processId)) as ApiResponse<JobStatusResponse>;
        const res = apiRes.data;
        if (!res) return;
        setJobs((prev) =>
          prev.map((j) =>
            j.processId === processId
              ? {
                  ...j,
                  status: res.status,
                  result: res.result,
                  error: res.error,
                  completedAt:
                    res.status === "completed" || res.status === "failed" || res.status === "pending_review"
                      ? Date.now()
                      : j.completedAt,
                }
              : j,
          ),
        );

        const job = jobs.find((j) => j.processId === processId);
        const isTerminal =
          res.status === "completed" || res.status === "failed" || res.status === "pending_review";
        if (isTerminal && job) {
          handleTerminalState(job, res.result, res.error);
        }
      } catch (e) {
        // Ignore transient poll errors.
      }
    },
    [handleTerminalState, jobs],
  );

  const addJob = useCallback(
    (processId: string, callId: string, description: string) => {
      setJobs((prev) => {
        if (prev.some((j) => j.processId === processId)) return prev;
        return [
          ...prev,
          {
            processId,
            callId,
            description,
            status: "running",
            result: null,
            error: null,
            startedAt: Date.now(),
          },
        ];
      });

      // Poll immediately, then every interval.
      pollJob(processId);
      if (timersRef.current[processId]) return;
      timersRef.current[processId] = setInterval(() => pollJob(processId), POLL_INTERVAL_MS);
    },
    [pollJob],
  );

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearInterval);
      timersRef.current = {};
    };
  }, []);

  const viewProcess = (processId: string) => {
    navigate(`/process-manager?process_id=${processId}`);
    setOpenPanel(false);
  };

  const activeCount = jobs.filter((j) => j.status === "running").length;
  const completedJobs = jobs.filter(
    (j) => j.status === "completed" || j.status === "pending_review",
  );

  return (
    <ProcessingJobsContext.Provider value={{ jobs, addJob, removeJob, openPanel, setOpenPanel }}>
      {children}

      {/* Floating minimized indicator */}
      {jobs.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
          {openPanel && (
            <div className="w-80 rounded-lg border border-border bg-card p-3 shadow-lg">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-semibold">Processing</h4>
                <button
                  onClick={() => setOpenPanel(false)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Hide
                </button>
              </div>
              <div className="space-y-2 max-h-72 overflow-auto">
                {jobs.map((job) => (
                  <div
                    key={job.processId}
                    className="rounded-md border border-border bg-background p-2 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      {job.status === "running" ? (
                        <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                      ) : job.status === "failed" ? (
                        <span className="h-2 w-2 rounded-full bg-red-500" />
                      ) : (
                        <span className="h-2 w-2 rounded-full bg-green-500" />
                      )}
                      <span className="flex-1 truncate font-medium">{job.description}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-label-2xs text-muted-foreground">
                      <span>
                        {job.status === "running"
                          ? `Elapsed ${Math.floor((Date.now() - job.startedAt) / 1000)}s`
                          : job.status}
                      </span>
                      {job.result?.process_id && (
                        <button
                          onClick={() => viewProcess(job.result!.process_id!)}
                          className="text-primary hover:underline"
                        >
                          View
                        </button>
                      )}
                    </div>
                    {job.error && <p className="mt-1 text-label-2xs text-red-600">{job.error}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!openPanel && (
            <button
              onClick={() => setOpenPanel(true)}
              className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium shadow-lg hover:bg-accent transition-colors"
            >
              {activeCount > 0 ? (
                <>
                  <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                  {activeCount} building
                  {completedJobs.length > 0 && ` · ${completedJobs.length} ready`}
                </>
              ) : (
                <>
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  {jobs.length} done
                </>
              )}
            </button>
          )}
        </div>
      )}
    </ProcessingJobsContext.Provider>
  );
}

export function useProcessingJobs() {
  const ctx = useContext(ProcessingJobsContext);
  if (!ctx) {
    throw new Error("useProcessingJobs must be used within ProcessingJobsProvider");
  }
  return ctx;
}
